import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { api } from '../lib/api';
import { debounce } from '../lib/utils';
import { markDirty, upsertProject } from '../lib/localCache';
function getSecret(state) {
    if (!state.secret)
        throw new Error('No secret');
    return state.secret;
}
function getProjectId(state) {
    if (!state.projectId)
        throw new Error('No project');
    return state.projectId;
}
export const useProjectStore = create()(subscribeWithSelector((set, get) => ({
    // Auth
    projectId: null,
    secret: null,
    role: null,
    // Data
    project: null,
    timelines: [],
    events: [],
    branches: [],
    merges: [],
    // UI
    selectedEventId: null,
    selectedTimelineId: null,
    editingEventId: null,
    sidebarTab: 'timelines',
    shareModalOpen: false,
    isSidebarOpen: true,
    viewport: { x: 0, y: 0, scale: 1 },
    isSaving: false,
    isLoading: false,
    error: null,
    // ── Data loading ──────────────────────────────────────────────────────────
    loadProject: async (projectId, secret) => {
        set({ isLoading: true, error: null });
        try {
            const data = await api.projects.get(projectId, secret);
            set({
                projectId,
                secret,
                role: data.role,
                project: data.project,
                timelines: data.timelines,
                events: data.events.sort((a, b) => a.dateValue.localeCompare(b.dateValue)),
                branches: data.branches,
                merges: data.merges,
                isLoading: false,
            });
            // Stash basic project metadata in the local cache so the dashboard
            // remembers this project even before the user hits "Save".
            upsertProject({
                id: data.project.id,
                name: data.project.name,
                description: data.project.description,
                editSecret: data.project.editSecret,
                viewSecret: data.project.viewSecret,
                settings: data.project.settings,
                updatedAt: data.project.updatedAt,
            });
        }
        catch (e) {
            set({ error: e.message, isLoading: false });
            throw e;
        }
    },
    reloadProject: async () => {
        const { projectId, secret } = get();
        if (projectId && secret)
            await get().loadProject(projectId, secret);
    },
    // ── Project ───────────────────────────────────────────────────────────────
    updateProjectMeta: async (data) => {
        const state = get();
        const projectId = getProjectId(state);
        const secret = getSecret(state);
        set({ isSaving: true });
        try {
            await api.projects.update(projectId, secret, data);
            set((s) => ({
                project: s.project ? { ...s.project, ...data } : null,
                isSaving: false,
            }));
            const { project } = get();
            if (project) {
                upsertProject({
                    id: project.id,
                    name: project.name,
                    description: project.description,
                    editSecret: project.editSecret,
                    viewSecret: project.viewSecret,
                    settings: project.settings,
                    updatedAt: new Date().toISOString(),
                });
                markDirty(true);
            }
        }
        catch (e) {
            set({ error: e.message, isSaving: false });
        }
    },
    regenerateSecrets: async () => {
        const state = get();
        const projectId = getProjectId(state);
        const secret = getSecret(state);
        const result = await api.projects.regenerateSecrets(projectId, secret);
        set((s) => ({
            secret: result.editSecret,
            project: s.project
                ? { ...s.project, editSecret: result.editSecret, viewSecret: result.viewSecret }
                : null,
        }));
        return result;
    },
    // ── Timelines ─────────────────────────────────────────────────────────────
    createTimeline: async (name, color) => {
        const state = get();
        const projectId = getProjectId(state);
        const secret = getSecret(state);
        set({ isSaving: true });
        try {
            const tl = await api.timelines.create(projectId, secret, { name, color });
            set((s) => ({ timelines: [...s.timelines, tl], isSaving: false }));
            return tl;
        }
        catch (e) {
            set({ error: e.message, isSaving: false });
            throw e;
        }
    },
    updateTimeline: async (tlId, data) => {
        const state = get();
        const secret = getSecret(state);
        set({ isSaving: true });
        try {
            await api.timelines.update(tlId, secret, data);
            set((s) => ({
                timelines: s.timelines.map((t) => t.id === tlId ? { ...t, ...data } : t),
                isSaving: false,
            }));
        }
        catch (e) {
            set({ error: e.message, isSaving: false });
        }
    },
    deleteTimeline: async (tlId) => {
        const state = get();
        const secret = getSecret(state);
        set({ isSaving: true });
        try {
            await api.timelines.delete(tlId, secret);
            set((s) => ({
                timelines: s.timelines.filter((t) => t.id !== tlId),
                events: s.events.filter((e) => e.timelineId !== tlId),
                branches: s.branches.filter((b) => b.sourceTimelineId !== tlId && b.targetTimelineId !== tlId),
                isSaving: false,
                selectedTimelineId: s.selectedTimelineId === tlId ? null : s.selectedTimelineId,
            }));
        }
        catch (e) {
            set({ error: e.message, isSaving: false });
        }
    },
    reorderTimelines: async (orderedIds) => {
        const state = get();
        const secret = getSecret(state);
        set((s) => ({
            timelines: orderedIds
                .map((id, order) => {
                const tl = s.timelines.find((t) => t.id === id);
                return tl ? { ...tl, order } : null;
            })
                .filter(Boolean),
        }));
        // Persist reorder
        await Promise.all(orderedIds.map((id, order) => api.timelines.update(id, secret, { order })));
    },
    // ── Events ────────────────────────────────────────────────────────────────
    createEvent: async (tlId, data) => {
        const state = get();
        const secret = getSecret(state);
        set({ isSaving: true });
        try {
            const ev = await api.events.create(tlId, secret, data);
            set((s) => ({
                events: [...s.events, ev].sort((a, b) => a.dateValue.localeCompare(b.dateValue)),
                isSaving: false
            }));
            return ev;
        }
        catch (e) {
            set({ error: e.message, isSaving: false });
            throw e;
        }
    },
    updateEvent: async (evId, data) => {
        const state = get();
        const secret = getSecret(state);
        // Optimistic update
        set((s) => ({
            events: s.events
                .map((e) => e.id === evId ? { ...e, ...data } : e)
                .sort((a, b) => a.dateValue.localeCompare(b.dateValue)),
            isSaving: true,
        }));
        try {
            const apiData = {};
            if (data.title !== undefined)
                apiData.title = data.title;
            if (data.subtitle !== undefined)
                apiData.subtitle = data.subtitle;
            if (data.dateType !== undefined)
                apiData.dateType = data.dateType;
            if (data.dateValue !== undefined)
                apiData.dateValue = data.dateValue;
            if (data.status !== undefined)
                apiData.status = data.status;
            if (data.position !== undefined) {
                apiData.positionX = data.position.x;
                apiData.positionY = data.position.y;
            }
            if (data.tags !== undefined)
                apiData.tags = data.tags;
            if (data.notes !== undefined)
                apiData.notes = data.notes;
            await api.events.update(evId, secret, apiData);
            set({ isSaving: false });
        }
        catch (e) {
            set({ error: e.message, isSaving: false });
            await get().reloadProject();
        }
    },
    updateEventStatus: async (evId, status) => {
        const state = get();
        const secret = getSecret(state);
        // Optimistic
        set((s) => ({
            events: s.events.map((e) => e.id === evId ? { ...e, status } : e),
        }));
        try {
            await api.events.update(evId, secret, { status });
        }
        catch (e) {
            set({ error: e.message });
            await get().reloadProject();
        }
    },
    deleteEvent: async (evId) => {
        const state = get();
        const secret = getSecret(state);
        set({ isSaving: true });
        try {
            await api.events.delete(evId, secret);
            set((s) => ({
                events: s.events.filter((e) => e.id !== evId),
                branches: s.branches.filter((b) => b.sourceEventId !== evId),
                selectedEventId: s.selectedEventId === evId ? null : s.selectedEventId,
                editingEventId: s.editingEventId === evId ? null : s.editingEventId,
                isSaving: false,
            }));
        }
        catch (e) {
            set({ error: e.message, isSaving: false });
        }
    },
    // ── Branches ──────────────────────────────────────────────────────────────
    createBranch: async (data) => {
        const state = get();
        const projectId = getProjectId(state);
        const secret = getSecret(state);
        set({ isSaving: true });
        try {
            const br = await api.branches.create(projectId, secret, data);
            set((s) => ({ branches: [...s.branches, br], isSaving: false }));
            return br;
        }
        catch (e) {
            set({ error: e.message, isSaving: false });
            throw e;
        }
    },
    updateBranch: async (brId, data) => {
        const state = get();
        const secret = getSecret(state);
        set({ isSaving: true });
        try {
            await api.branches.update(brId, secret, data);
            set((s) => ({
                branches: s.branches.map((b) => b.id === brId ? { ...b, ...data } : b),
                isSaving: false,
            }));
        }
        catch (e) {
            set({ error: e.message, isSaving: false });
        }
    },
    deleteBranch: async (brId) => {
        const state = get();
        const secret = getSecret(state);
        await api.branches.delete(brId, secret);
        set((s) => ({ branches: s.branches.filter((b) => b.id !== brId) }));
    },
    // ── Merges ────────────────────────────────────────────────────────────────
    createMerge: async (data) => {
        const state = get();
        const projectId = getProjectId(state);
        const secret = getSecret(state);
        const m = await api.merges.create(projectId, secret, data);
        set((s) => ({ merges: [...s.merges, m] }));
        return m;
    },
    deleteMerge: async (mId) => {
        const state = get();
        const secret = getSecret(state);
        await api.merges.delete(mId, secret);
        set((s) => ({ merges: s.merges.filter((m) => m.id !== mId) }));
    },
    // ── UI ────────────────────────────────────────────────────────────────────
    selectEvent: (id) => set({ selectedEventId: id, sidebarTab: id ? 'event' : 'timelines' }),
    selectTimeline: (id) => set({ selectedTimelineId: id }),
    setEditingEvent: (id) => set({ editingEventId: id }),
    setSidebarTab: (tab) => set({ sidebarTab: tab, isSidebarOpen: true }),
    setSidebarOpen: (open) => set({ isSidebarOpen: open }),
    toggleSidebar: () => set((s) => ({ isSidebarOpen: !s.isSidebarOpen })),
    setShareModalOpen: (open) => set({ shareModalOpen: open }),
    setViewport: (vp) => set((s) => ({ viewport: { ...s.viewport, ...vp } })),
    clearError: () => set({ error: null }),
})));
// Auto-save settings with debounce
const _autoSave = debounce(async () => {
    const state = useProjectStore.getState();
    if (state.role !== 'edit' || !state.project?.settings?.autoSave)
        return;
    if (state.project) {
        try {
            await api.projects.update(state.projectId, state.secret, {
                settings: state.project.settings,
            });
        }
        catch {
            // silent
        }
    }
}, 30_000);
// Expose auto-save trigger
export const triggerAutoSave = _autoSave;
